import prettyMS from 'pretty-ms'

import { Entry } from '@/models/catalog'
import ShortTime from '@/utils/shorttime'
import fieldRules from '@/utils/rules'

// ERRORS

export class JournalError extends Error {
	constructor(...args) {
		super(...args)
		// Maintains proper stack trace for where our error was thrown (only available on V8)
		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, JournalError)
		}
		this.name = 'JournalError'
	}
}
export class RecordError extends JournalError {
	constructor(record = undefined, ...args) {
		super(...args)
		this.record = record
		// Maintains proper stack trace for where our error was thrown (only available on V8)
		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, RecordError)
		}
		this.name = 'RecordError'
	}
}

// CLASSES

export class Record {
	// members
	entry
	time = {
		begin: undefined,
		end: undefined,
	}
	joker = false
	autostop = false // determines if the next record will stop this one; may be true (will stop), or false
	modified = false
	comment
	qty = 0

	constructor(entry, tm, comment = '') {
		if (!(entry instanceof Entry)) {
			throw new RecordError()
		}
		this.entry = entry

		this.time = {
			begin: new ShortTime(tm),
			end: undefined,
		}
		// others
		this.joker = false
		this.autostop = undefined
		this.modified = false
		this.comment = comment
	}
	get type() {
		return this.entry.type
	}
	isRunning() {
		return this.time.end === undefined
	}
	getDuration() {
		if (this.time.end === undefined) return Date.now() - this.time.begin.tm
		else return this.time.end.tm - this.time.begin.tm
	}
	getPrettyDuration() {
		// return the running time, updated every sec (see created())
		return prettyMS(this.getDuration(), {
			secondsDecimalDigits: 0,
		})
	}
	isJoker() {
		return this.joker
	}
	isAutoStoppable() {
		return undefined
	}
	disableAutoStop() {
		if (!this.isAutoStoppable() || !this.isRunning()) return
		this.autostop = false
		return false
	}
	isEditable(field) {
		switch (field) {
			case 'label':
				return true
			case 'comment':
				return true
			case 'code':
				return true
			case 'qty':
				return true
			case 'cat1':
				return this.isJoker()
			case 'cat2':
				return this.isJoker()
			case 'cat3':
				return this.isJoker()
			default:
				return false
		}
	}
	getEditValue(field) {
		// return the prop value, in a editable way (see times)
		switch (field) {
			case 'code':
				return this.code || this.entry.code
			case 'qty':
				return this.qty
			case 'label':
				return this.entry.label
			case 'comment':
				return this.comment || ''
			case 'cat1':
				return this.entry.cats[0] || ''
			case 'cat2':
				return this.entry.cats[1] || ''
			case 'cat3':
				return this.entry.cats[2] || ''
			default:
				return undefined
		}
	}
	validateValue(field, val) {
		// return true if OK, or false / a string otherwise
		const rules = fieldRules[field]
		if (!rules) {
			console.log(`no rules for field '${field}'`)
			return true
		}
		for (const r of rules) {
			const result = r(val)
			if (result !== true) return result
		}
		return true
	}

	checkEditValue(field, val) {
		// update a prop value, given an edition input
		// check that field is editable for this record (should not happen)
		if (!this.isEditable(field))
			throw new RecordError(
				`Cannot edit field '${field}': field is not editable`,
			)

		// validate value before update
		const validationResult = this.validateValue(field, val)
		if (validationResult !== true) {
			throw new RecordError(
				`Validation failed for field '${field}': ` +
					(validationResult === false ? 'unknown error' : validationResult),
			)
		}
	}
}

export class Event extends Record {
	constructor(entry, tm, comment = '') {
		super(entry, tm, comment)
		// in an Event, end == begin
		this.time.end = this.time.begin
	}

	isAutoStoppable() {
		return false
	}
}

export class Task extends Record {
	constructor(entry, tm, comment = '') {
		super(entry, tm, comment)
		this.autostop = true // autostop tasks by default
	}
	stop(tm = Date.now()) {
		if (!this.isRunning()) return
		this.time.end = new ShortTime(tm)
	}
	isAutoStoppable() {
		return true
	}
}

export class Interval extends Record {
	constructor(entry, begin_tm, end_tm, comment = '') {
		super(entry, begin_tm, comment)
		// in an Interval, end is provided at constructor
		this.time.end = new ShortTime(end_tm)
	}

	isAutoStoppable() {
		return false
	}
}

// METHODS

export function cloneEntry(entry) {
	if (!(entry instanceof Entry)) {
		throw new RecordError()
	}

	const line = {
		cat1: entry.cats[0] || '',
		cat2: entry.cats[1] || '',
		cat3: entry.cats[2] || '',
		type: entry.type,
		label: entry.label,
		code: entry.code || '',
		description: entry.description || '',
	}

	return new Entry(line)
}

export function makeRecord(entry, tm = Date.now(), comment = '') {
	const localEntry = cloneEntry(entry)

	switch (localEntry.type) {
		case 'event':
			return new Event(localEntry, tm, comment)
		case 'task':
			return new Task(localEntry, tm, comment)
		default:
			throw new RecordError(`Invalid entry type '${localEntry.type}'`)
	}
}

export function makeJokerRecord(type, label, tm = Date.now(), comment = '') {
	const line = {
		cat1: 'joker',
		cat2: type,
		cat3: '',
		type,
		label,
		code: '',
		description: '',
	}
	const rec = makeRecord(new Entry(line), tm, comment)
	rec.joker = true
	return rec
}

export function makeInterval(begin_tm, end_tm, comment = '') {
	const line = {
		cat1: 'interval',
		cat2: '',
		cat3: '',
		type: 'interval',
		label: 'interval',
		code: '',
		description: '',
	}
	const rec = new Interval(new Entry(line), begin_tm, end_tm, comment)
	return rec
}

export default class Journal {
	// members
	records = []

	getDuration() {
		if (this.records.length === 0) return undefined
		else return Date.now() - this.records[-1].begin.tm
	}

	getPrettyDuration() {
		return prettyMS(Date.now() - this.origin, {
			secondsDecimalDigits: 0,
		})
	}

	get origin() {
		return this.records[this.records.length - 1].time.begin.tm
	}

	add(record) {
		// autostop the previous task if applicable
		if (this.records.length > 0) {
			const autostoppable = this.records.find(r => r.isRunning() && r.autostop)
			if (autostoppable) {
				// we found one, stop it
				autostoppable.stop(record.time.begin.tm)
			} else {
				// none found, so we must insert an interval
				const intervalEnd = record.time.begin.tm
				// get the first record with autostop !== true (any event, or task w/ autostop != true)
				const lastRecord = this.records.find(
					r => r.type !== 'task' || r.autostop,
				)
				// get the interval begin, either end of lastRecord, or the very beginning of journal
				const intervalBegin = lastRecord ? lastRecord.time.end.tm : this.origin
				// insert an interval
				this.records.unshift(makeInterval(intervalBegin, intervalEnd))
			}
		}
		// stack the new record up
		this.records.unshift(record)
	}

	remove(record) {
		const index = this.records.indexOf(record)
		if (index > -1) {
			this.records.splice(index, 1)
		}
	}
}
